import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from "recharts";

const COLORS = ["hsl(176 62% 30%)", "hsl(38 92% 50%)", "hsl(210 64% 46%)", "hsl(340 60% 52%)", "hsl(265 50% 55%)", "hsl(150 60% 34%)"];

export default function SupplierAnalysis() {
  const { data } = useQuery({ queryKey: ["analysis"], queryFn: async () => (await api.get("/analysis/suppliers")).data });
  const suppliers = data?.suppliers || [];
  const categories = data?.categories || [];
  const top = data?.top || [];

  return (
    <div>
      <PageTitle title="Supplier Data Analysis" subtitle="Purchase distribution and outstanding by supplier & category" />

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Card className="p-5">
          <h3 className="font-display font-bold mb-4">Purchase by Category</h3>
          <div className="h-72">
            {categories.length === 0 ? <div className="h-full grid place-items-center text-muted-foreground text-sm">No data yet.</div> : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={categories} dataKey="purchase" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(e) => e.category}>
                    {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => inr(v)} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-bold mb-4">Top 5 Suppliers by Purchase</h3>
          <div className="space-y-3">
            {top.length === 0 ? <div className="text-muted-foreground text-sm">No data yet.</div> : top.map((s, i) => {
              const max = top[0].purchase || 1;
              return (
                <div key={i}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium">{s.name}</span><span className="data-num">{inrShort(s.purchase)}</span></div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.purchase / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card className="overflow-hidden mb-4">
        <div className="p-4 border-b"><h3 className="font-display font-bold">Category Summary</h3></div>
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Category</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Purchase</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
            <TableBody>
              {categories.map((c, i) => (
                <TableRow key={i}><TableCell className="font-medium">{c.category}</TableCell><TableCell className="text-right">{c.count}</TableCell><TableCell className="text-right data-num">{inr(c.purchase)}</TableCell><TableCell className="text-right data-num text-destructive">{inr(c.balance)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="p-4 border-b"><h3 className="font-display font-bold">All Suppliers</h3></div>
        <div className="overflow-x-auto ssacc-scroll">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Bills</TableHead><TableHead className="text-right">Purchase</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
            <TableBody>
              {suppliers.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No data.</TableCell></TableRow> : suppliers.map((s, i) => (
                <TableRow key={i}><TableCell className="font-semibold text-primary">{s.supplier_code}</TableCell><TableCell>{s.name}</TableCell><TableCell>{s.category}</TableCell><TableCell className="text-right">{s.count}</TableCell><TableCell className="text-right data-num">{inr(s.purchase)}</TableCell><TableCell className="text-right data-num text-success">{inr(s.paid)}</TableCell><TableCell className="text-right data-num text-destructive">{inr(s.balance)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
